<template>
  <div class="work-order-source">
      <div class="source-container">
        <div class="chart-section">
          <div ref="chartRef" class="chart"></div>
        </div>
        
        <div class="source-stats">
          <div class="total-info">
            <div class="total-label">{{ $t('总工单数') }}</div>
            <div class="total-value">{{ formatNumber(totalCount) }}</div>
          </div>
          
          <div class="source-items">
            <div class="source-item" 
                 v-for="(item, index) in sourceData" 
                 :key="index"
                 :class="{ 'active': activeIndex === index }"
                 @mouseenter="activeIndex = index"
                 @mouseleave="activeIndex = null">
              
              <div class="source-icon" :style="{ backgroundColor: colors[index % colors.length] }">
                <i :class="getSourceIcon(item.name)"></i>
              </div>
              
              <div class="source-content">
                <div class="source-name">{{ item.name }}</div>
                <div class="source-count">{{ formatNumber(item.value) }} {{ $t('件') }}</div>
                <div class="source-percent">{{ item.percent }}%</div>
                
                <div class="progress-bar">
                  <div class="progress-fill" 
                       :style="{ 
                         width: item.percent + '%',
                         backgroundColor: colors[index % colors.length]
                       }">
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { postAction } from '@/api/manage'
export default {
  name: 'WorkOrderSource',
  components: {

  },
  data() {
    return {
      chart: null,
      sourceData: [],
      totalCount: 0,
      activeIndex: null,
      colors: ['#52C4DF', '#5D6A94', '#8BA6C6', '#A8C6F3'],
      url: {
        queryTicketsSourceTypeNum: "/jeecg-engineering/ticket/index/largeScreen/queryTicketsSourceTypeNum"
      }
    }
  },
  mounted() {
    this.init()
  },
  beforeDestroy() {
    this.disposeChart()
  },
  methods: {
    async init() {
      await this.getData()
      this.initChart()
    },
    async getData() {
      try {
        const res = await postAction(this.url.queryTicketsSourceTypeNum, {})
        if (res.success && res.result) {
          const data = res.result
          this.totalCount = data.reduce((sum, item) => sum + (item.num || 0), 0)
          
          this.sourceData = data.map(item => ({
            name: this.getSourceTypeName(item.typeName),
            value: item.num || 0,
            percent: this.totalCount > 0 ? ((item.num / this.totalCount) * 100).toFixed(1) : 0
          }))
        } else {
          this.loadMockData()
        }
      } catch (error) {
        console.error('获取工单来源数据失败:', error)
        this.loadMockData()
      }
    },
    loadMockData() {
      this.totalCount = 1245
      this.sourceData = [
        { name: 'PC端', value: 567, percent: '45.5' },
        { name: '微信小程序', value: 389, percent: '31.2' },
        { name: '公众号', value: 189, percent: '15.2' },
        { name: '客服热线', value: 100, percent: '8.0' }
      ]
    },
    getSourceTypeName(sourceType) {
      const sourceMap = {
        0: this.$t('PC端'),
        1: this.$t('微信小程序'),
        2: this.$t('公众号'),
        3: this.$t('客服热线'),
        'PC': this.$t('PC端'),
        'MiniProgram': this.$t('微信小程序'),
        'WeChat': this.$t('公众号'),
        'Phone': this.$t('客服热线'),
      }
      return sourceMap[sourceType] || sourceType || this.$t('其他')
    },
    getSourceIcon(sourceName) {
      const iconMap = {
        [this.$t('PC端')]: 'el-icon-monitor',
        [this.$t('微信小程序')]: 'el-icon-chat-dot-round',
        [this.$t('公众号')]: 'el-icon-chat-line-round',
        [this.$t('客服热线')]: 'el-icon-phone',
      }
      return iconMap[sourceName] || 'el-icon-question'
    },
    initChart() {
      this.disposeChart()
      
      if (!this.$refs.chartRef) return
      
      this.chart = echarts.init(this.$refs.chartRef)
      
      const option = {
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(0,11,42,0.9)',
          borderColor: '#5D6A94',
          borderWidth: 1,
          borderRadius: 8,
          textStyle: {
            color: '#CEE2FF'
          },
          formatter: (params) => {
            const item = params[0]
            return `
              <div style="padding: 8px;">
                <div style="font-size: 14px; margin-bottom: 5px; color: #fff;">${item.name}</div>
                <div style="font-size: 12px; color: #00ffc8;">
                  ${this.$t('数量')}: ${item.value} ${this.$t('件')}
                </div>
                <div style="font-size: 12px; color: #00ffc8;">
                  ${this.$t('占比')}: ${((item.value / this.totalCount) * 100).toFixed(1)}%
                </div>
              </div>
            `
          }
        },
        grid: {
          left: '5%',
          right: '12%',
          bottom: '5%',
          top: '5%',
          containLabel: true
        },
        xAxis: {
          type: 'value',
          max: this.totalCount,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { 
            color: '#8BA6C6',
            fontSize: 10
          },
          splitLine: {
            lineStyle: {
              color: 'rgba(0, 170, 255, 0.1)'
            }
          }
        },
        yAxis: {
          type: 'category',
          data: this.sourceData.map(item => item.name),
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { 
            color: '#8BA6C6',
            fontSize: 10
          }
        },
        series: [
          {
            name: this.$t('工单来源'),
            type: 'bar',
            data: this.sourceData.map((item, index) => ({
              value: item.value,
              itemStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                  { offset: 0, color: this.colors[index % this.colors.length] },
                  { offset: 1, color: this.adjustColor(this.colors[index % this.colors.length], -0.3) }
                ]),
                borderRadius: [0, 3, 3, 0]
              }
            })),
            barWidth: '65%',
            label: {
              show: true,
              position: 'right',
              color: '#fff',
              fontSize: 10,
              formatter: (params) => {
                const percent = ((params.value / this.totalCount) * 100).toFixed(1);
                return `${params.value}${this.$t('件')} (${percent}%)`;
              }
            },
            emphasis: {
              itemStyle: {
                shadowBlur: 8,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 170, 255, 0.3)'
              }
            }
          }
        ]
      }
      
      this.chart.setOption(option)
      
      // 图表渲染完成
      
      window.addEventListener('resize', this.handleResize)
    },
    formatNumber(num) {
      return num.toLocaleString()
    },
    adjustColor(color, amount) {
      const hex = color.replace('#', '')
      const r = Math.max(0, Math.min(255, parseInt(hex.substr(0, 2), 16) + Math.floor(amount * 255)))
      const g = Math.max(0, Math.min(255, parseInt(hex.substr(2, 2), 16) + Math.floor(amount * 255)))
      const b = Math.max(0, Math.min(255, parseInt(hex.substr(4, 2), 16) + Math.floor(amount * 255)))
      return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
    },
    handleResize() {
      if (this.chart) {
        this.chart.resize()
      }
    },
    disposeChart() {
      if (this.chart) {
        this.chart.dispose()
        this.chart = null
        window.removeEventListener('resize', this.handleResize)
      }
    }
  }
}
</script>

<style lang="less" scoped>
.work-order-source {
  height: 100%;
  padding: 10px;
}

.source-container {
  display: flex;
  height: 100%;
  gap: 15px;
}

.chart-section {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  max-height: 100%;
}

.chart {
  width: 100%;
  height: 100%;
  max-height: 220px;
}

.source-stats {
  flex: 0 0 45%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.total-info {
  text-align: center;
  margin-bottom: 4px;
  padding: 5px;
  background: rgba(0, 48, 111, 0.3);
  border: 1px solid rgba(0, 170, 255, 0.2);
  border-radius: 8px;
}

.total-label {
  color: #b6deff;
  font-size: 14px;
  // margin-bottom: 5px;
}

.total-value {
  color: #00E4FF;
  font-size: 27px;
  font-weight: bold;
  font-family: 'ds-digital-bold';
}

.source-items {
  // display: flex;
  // flex-direction: column;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 6px;
  flex: 1;
}

.source-item {
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(0, 48, 111, 0.3);
  border: 1px solid rgba(0, 170, 255, 0.2);
  border-radius: 6px;
  padding: 6px 8px;
  transition: all 0.3s ease;
  cursor: pointer;
  min-height: 42px;
  
  &:hover,
  &.active {
    background: rgba(0, 48, 111, 0.5);
    border-color: rgba(0, 170, 255, 0.4);
    transform: translateX(1px);
  }
}

.source-icon {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.3s ease;
  
  i {
    color: #fff;
    font-size: 10px;
  }
}

.source-content {
  flex: 1;
}

.source-name {
  color: #fff;
  font-size: 10px;
  font-weight: 500;
  // margin-bottom: 4px;
}

.source-count {
  color: #52C4DF;
  font-size: 10px;
  font-weight: bold;
  // margin-bottom: 2px;
}

.source-percent {
  color: #b6deff;
  font-size: 10px;
  // margin-bottom: 6px;
}

.progress-bar {
  width: 100%;
  height: 4px;
  background: rgba(8, 48, 111, 0.6);
  border-radius: 2px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  border-radius: 2px;
  transition: width 0.8s ease;
}

@media (max-width: 1600px) {
  .source-container {
    flex-direction: column;
  }
  
  .chart-section {
    flex: none;
    height: 50%;
  }
  
  .source-stats {
    flex: none;
    height: 50%;
  }
  
  .source-items {
    gap: 8px;
  }
  
  .source-item {
    padding: 8px;
  }
}

@media (max-width: 1200px) {
  .source-name {
    font-size: 11px;
  }
  
  .source-count,
  .source-percent {
    font-size: 10px;
  }
  
  .source-icon {
    width: 32px;
    height: 32px;
    
    i {
      font-size: 16px;
    }
  }
}
</style>